import {Alert, Autocomplete, Box, Button, Popover, Stack, TextField, Typography} from "@mui/material";
import {HTMLAttributes, useState} from "react";
import {KeySnapshot} from "../../api/Model";
import {useKeyDataStore} from "../../api/KeyDataStore";
import {useMessage} from "../../util/actions/Message";

export function BindNfcPopover({open, allKeys, anchorEl, transponderId, onClose, onBind}: {
    open: boolean,
    anchorEl: Element | null,
    transponderId: string | undefined,
    allKeys: KeySnapshot[],
    onClose: () => void,
    onBind: (keySnapshot: KeySnapshot) => void,
}) {
    const [selectedKey, setSelectedKey] = useState<KeySnapshot | null>(null);
    const [loading, setLoading] = useState(false);
    const {bindNFC} = useKeyDataStore();
    const message = useMessage();

    const handleBind = async () => {
        if (!selectedKey || !transponderId) return;
        setLoading(true);
        try {
            const updatedKey = await bindNFC(selectedKey.key, transponderId);
            message.success(`Bound NFC tag to key ${updatedKey.keyNumber}`);
            onBind({key: updatedKey, historyRecord: selectedKey.historyRecord});
            setSelectedKey(null);
        } catch {
            message.error('Failed to bind NFC tag');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedKey(null);
        onClose();
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            transformOrigin={{vertical: 'top', horizontal: 'left'}}
        >
            <Box sx={{p: 2, minWidth: 360}}>
                <Stack spacing={1.5}>
                    <Typography variant="h6">Unknown NFC Tag</Typography>
                    <Typography variant="body2" pb={2} color="text.secondary">Bind NFC tag {transponderId} to a key</Typography>
                    <Autocomplete<KeySnapshot>
                        options={allKeys}
                        getOptionLabel={s => s.key.keyNumber.toString()}
                        getOptionKey={s => `${s.key.id}-${s.historyRecord.id}`}
                        value={selectedKey}
                        onChange={(_, v) => setSelectedKey(v)}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Key number"
                                size="small"
                                autoFocus
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                {(selectedKey?.key?.transponderId ? (
                                                    <Typography variant="caption">NFC: {selectedKey.key.transponderId}</Typography>
                                                ): undefined)}
                                                {params.InputProps.endAdornment}
                                            </Stack>
                                        )
                                    }
                                }}
                            />
                        )}
                        renderOption={(optionProps: HTMLAttributes<HTMLLIElement> & { key: any }, keyState: KeySnapshot) => {
                            return (
                                <Box {...optionProps} key={optionProps.key} component="li" sx={{width: '100%'}}>
                                    <Stack width="100%" direction="row" gap={1} alignItems="center" justifyContent="space-between">
                                        <Box>{keyState.key.keyNumber}</Box>
                                        {keyState.key.transponderId && (
                                            <Typography variant="caption">NFC: {keyState.key.transponderId}</Typography>
                                        )}
                                    </Stack>
                                </Box>
                            );
                        }}
                        size="small"
                    />
                    {selectedKey?.key?.transponderId && (
                        <Alert severity="warning">
                            The key already has NFC
                        </Alert>
                    )}
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={handleClose}>Cancel</Button>
                        <Button
                            size="small"
                            variant="contained"
                            color={selectedKey && selectedKey.key.transponderId ? 'warning' : 'primary'}
                            disabled={!selectedKey || loading}
                            loading={loading}
                            onClick={handleBind}
                        >
                            {selectedKey && selectedKey.key.transponderId ? 'Bind Anyway' : 'Bind'}
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </Popover>
    );
}
